import { useEffect, useState } from 'react';
import { Icon } from '../icons';
import { OwlLogo } from '../shared';

// `beforeinstallprompt` is not in the standard DOM lib types.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'owl.m.installDismissed';

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/**
 * Android/Chrome "add to home screen" banner. Shows once the browser fires
 * `beforeinstallprompt` (only when installable), unless already installed or
 * previously dismissed. iOS has no programmatic prompt, so nothing shows there.
 */
export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (window.localStorage.getItem(DISMISS_KEY) === '1') return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!visible || !deferred) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
  };

  const install = async () => {
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch {
      // ignore
    } finally {
      setVisible(false);
      setDeferred(null);
    }
  };

  return (
    <div className="m-install">
      <div className="m-install-mark">
        <OwlLogo size={22} />
      </div>
      <div className="m-install-body">
        <div className="t">Instala OWL</div>
        <div className="s">Añádela a tu pantalla de inicio</div>
      </div>
      <button type="button" className="m-install-btn" onClick={install}>
        Instalar
      </button>
      <button type="button" className="m-install-x" onClick={dismiss} aria-label="Descartar">
        <Icon.X size={15} />
      </button>
    </div>
  );
}
